import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import queryString from 'query-string-for-all';

import { useHistory, useLocation } from 'react-router-dom';
import Loader from 'src/components/loader';
import { PageContent } from 'src/components/page';

import { productCollectionStaticReducer } from './reducers/static-actions';

import type {
  AggregationSection,
  Exact,
  GlobalFilterSearchQuery,
  ProductCollection as ProductCollectionType,
  ProductCollectionAggregationQuery,
  ProductCollectionFilter,
  ProductCollectionFilterInput,
  ProductCollectionPageQueryVariables,
  ProductCollectionQueryInput,
  ProductsSlice,
} from '../../types';

import RouteContext from '../../routes/context';

import { useMatchedRoute } from 'src/hooks/use-matched-route';
import { mergeIntoQueryArray } from './utils/product-collections';

// RQ Queries
import { useGlobalFilterSearchQuery } from './queries/useProductCollectionGlobalFilterSearchQuery';
import { useProductCollectionPageQuery } from './queries/useProductCollectionPageQuery';
import { useProductCollectionSearchQuery } from './queries/useProductCollectionSearchQuery';
import { ProductCollection } from './components/product-collection';

// TODO: These are not final types. After the resolvers are properly typed, we can remove all the casts from this file as well
export type RouteState = {
  initialLoad: boolean;
  id: ProductCollectionType['id'];
  slug: string;
  query: ProductCollectionQueryInput;
  filters: ProductCollectionFilter[];
  aggregations: ProductCollectionAggregationQuery['productCollectionAggregation'][];
  productPropertyIds: string[];
  productPropertyColumnIds: string[];
  propertyColumnIds: string[];
  globalFilterSearchData: GlobalFilterSearchQuery;
  sort: string | string[];
  offset: number;
  url: string | null;
  direction: string | string[];
};

const ProductCollectionLoader = () => {
  const route = useMatchedRoute();
  const location = useLocation();
  const history = useHistory();
  const [fetchedUrls, setFetchedUrls] = useState<
    Set<string | null | undefined>
  >(new Set());

  function addToFetchedUrls(newUrl: string) {
    setFetchedUrls(prevSet => new Set([...prevSet, newUrl]));
  }

  // SSR
  const initRouteState = {
    ...route?.ssr({ location }),
    aggregations: [],
  } as RouteState;

  // RQ: Initial Product Collection Query Variables
  const initProductCollectionQueryVariables: ProductCollectionPageQueryVariables =
    {
      ...route?.variables,
      ...route?.component.stateToVariables(initRouteState),
    };

  const [routeState, setRouteState] = useState<RouteState>(initRouteState);

  // toggle between global search aggs and quick filter aggregations
  const globalAggs = useRef(false);
  const toggleGlobalAggs = (value: boolean) => {
    globalAggs.current = value;
  };
  // global or quick filter aggregations state
  const [aggData, setAggData] = useState<AggregationSection[]>([]);

  // ------------------ RQ QUERIES: START ------------------
  const [productCollectionQueryVariables, setProductCollectionQueryVariables] =
    useState<ProductCollectionPageQueryVariables>(
      initProductCollectionQueryVariables
    );

  const {
    data: pcData,
    isLoading: pcIsLoading,
    isSuccess: pcIsSuccess,
  } = useProductCollectionPageQuery(productCollectionQueryVariables);

  const globalFiltersSSRQueryVariables: Exact<{
    definitionId: string;
    query: ProductCollectionQueryInput;
  }> = {
    definitionId: pcData?.productCollection?.id ?? '',
    query: {
      filters: routeState?.filters as ProductCollectionFilterInput[],
      globalPropertySearch: '',
    },
  };

  const [globalFiltersQueryVariables, setGlobalFiltersQueryVariables] =
    useState(globalFiltersSSRQueryVariables);

  const {
    data: globalFiltersData,
    isLoading: globalFiltersIsLoading,
    isSuccess: globalFiltersIsSuccess,
  } = useGlobalFilterSearchQuery(globalFiltersQueryVariables);

  const [globalSeachQueryVariables, setSearchQueryVariables] = useState({});

  const {
    data: pcSearchData,
    isLoading: pcSearchIsLoading,
    isSuccess: pcSearchIsSuccess,
  } = useProductCollectionSearchQuery(globalSeachQueryVariables);
  // ------------------ RQ QUERIES: END ------------------

  // ------------------ RQ AGGREGATIONS VARIABLES: START ------------------
  // getStateFromReducer regular reducer actions
  const setRegularReducerVariables = useCallback(
    (action: any) => {
      const newRouteState = productCollectionStaticReducer(routeState, action);

      const newProductCollectionQueryVariables: ProductCollectionPageQueryVariables =
        {
          ...route?.variables,
          ...route?.component.stateToVariables(newRouteState),
        };

      setRouteState(newRouteState as RouteState);

      // re-triggers RQ query for page data
      setProductCollectionQueryVariables(newProductCollectionQueryVariables);
    },
    [route?.component, route?.variables, routeState]
  );

  // AGGREGATION_MORE_ITEMS, AGGREGATION_SEARCH
  const setAggregationVariables = useCallback(
    (action: any) => {
      const { query, size } = action;
      const newAggregation = {
        ...routeState.aggregations?.find(
          agg => agg && 'id' in agg && agg?.id === action.id
        ),
        id: action.id,
        ...(query !== undefined && { query }),
        ...(size && { size }),
      } as ProductCollectionAggregationQuery['productCollectionAggregation'] & {
        size: number;
        query: string;
      };

      const aggVariables = {
        id: routeState.id,
        aggregationId: action.id,
        query: {
          filters: routeState.filters,
          aggregations: [newAggregation],
        },
      };

      const newAggregations = mergeIntoQueryArray(
        // TODO: fix generic type
        // @ts-expect-error fix generic type
        routeState.aggregations,
        newAggregation,
        'id'
      );

      toggleGlobalAggs(false);

      setRouteState({
        ...routeState,
        aggregations: newAggregations,
      });

      // re-triggers RQ query agg search
      setSearchQueryVariables(aggVariables);
    },
    [routeState]
  );

  // GLOBAL_AGGREGATION_SEARCH
  const setGlobalAggregationVariables = useCallback(
    (action: any) => {
      const variables = {
        definitionId: routeState.id,
        query: {
          filters: routeState.filters,
          globalPropertySearch: action.globalPropertySearch,
        },
      } as Exact<{
        definitionId: string;
        query: ProductCollectionQueryInput;
      }>;

      toggleGlobalAggs(true);

      // GLOBAL_AGGREGATION_SEARCH + SET_QUERY (sets aggregations to [] in the current solution)
      setRouteState({
        ...routeState,
        aggregations: [],
      });

      // re-triggers RQ query global global agg search
      setGlobalFiltersQueryVariables(variables);
    },
    [routeState]
  );
  // ------------------ RQ AGGREGATIONS VARIABLES: END ------------------

  // replaces ROUTE_DATA_REFETCHED action
  // updates history based on the new product collection url from API
  const updateHistory = useCallback(
    (fetchedUrl: string | null, shouldPushToHistory: boolean) => {
      if (fetchedUrl) {
        const { offset, sort, direction } = routeState;
        const { url, query } = queryString.parseUrl(fetchedUrl);
        const finalQuery = {
          ...query,
          ...(offset && offset > 0 && { offset: offset.toString() }),
          ...(sort && { sort }),
          ...(direction && { direction }),
        };
        const queryStr = Object.keys(finalQuery).length
          ? `?${queryString.stringify(finalQuery)}`
          : '';
        const newUrl = `${url}${queryStr}`;
        if (shouldPushToHistory) {
          history.push(newUrl, {
            refreshRoute: false,
          });
        } else {
          history.replace(newUrl, {
            refreshRoute: false,
          });
        }
      }
    },
    [history, routeState]
  );

  useEffect(() => {
    // RQ: GLOBAL_AGGREGATION_SEARCH
    if (
      !globalFiltersIsLoading &&
      globalFiltersIsSuccess &&
      globalAggs.current
    ) {
      // global filters loaded
      setAggData(
        (globalFiltersData?.globalFilterSearch as AggregationSection[]) ?? []
      );
    }

    // RQ: quick filters aggregation search
    if (
      !pcSearchIsLoading &&
      pcSearchIsSuccess &&
      !globalAggs.current &&
      pcSearchData?.productCollectionAggregation
    ) {
      setAggData([
        pcSearchData?.productCollectionAggregation,
      ] as AggregationSection[]);
    }

    // RQ: regular PC updates when RQ re-fetch happens and replicates ROUTE_LOADED action from the old solution
    if (!pcIsLoading && pcIsSuccess) {
      const pc = pcData?.productCollection;

      // Updates history every time we re-fetch a product collection
      // replaces ROUTE_DATA_REFETCHED action

      // replicates ROUTE_LOADED action
      if (!routeState.initialLoad) {
        const productsSlice = pc?.slices?.find(
          slice => slice.__typename === 'ProductsSlice'
        ) as ProductsSlice;

        const propertyColumnIds = productsSlice?.propertyColumns?.map(
          ({ id }) => id
        );

        // on initial load add url to fetched urls state
        if (pc?.url && !fetchedUrls.has(pc?.url)) addToFetchedUrls(pc?.url);

        setRouteState({
          ...routeState,
          initialLoad: true,
          id: pc?.id,
          url: null,
          filters: pc?.filters as ProductCollectionFilter[],
          propertyColumnIds,
        });
      }

      if (routeState.initialLoad) {
        updateHistory(pc?.url ?? null, !fetchedUrls.has(pc?.url));
        if (pc?.url && !fetchedUrls.has(pc?.url)) addToFetchedUrls(pc?.url);
      }
    }

    // update urls when using broswer actions and update component state
    const listenToHistory = history.listen(newLocation => {
      setRegularReducerVariables({
        type: 'URL_CHANGE',
        location: newLocation,
      });
    });

    return () => {
      listenToHistory();
    };
    // doesn't need routeState and updateHistory as dependency. Better way to do this?
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    globalFiltersData,
    globalFiltersIsSuccess,
    pcData,
    pcIsSuccess,
    pcSearchIsSuccess,
    pcSearchData,
    globalFiltersIsLoading,
    pcIsLoading,
    pcSearchIsLoading,
    history,
  ]);

  // ROUTE CONTEXT. Preserving the dispatch pattern for now
  const routeContextValue = useMemo(() => {
    return {
      dispatchAggregationAction: (action: any) => {
        switch (action.type) {
          case 'AGGREGATION_SEARCH':
          case 'AGGREGATION_MORE_ITEMS':
            setAggregationVariables(action);
            break;
          case 'GLOBAL_AGGREGATION_SEARCH':
            setGlobalAggregationVariables(action);
            break;
          default:
            break;
        }
      },
      dispatch: (action: any) => {
        setRegularReducerVariables(action);
      },
      currentRouteState: routeState,
    };
  }, [
    routeState,
    setAggregationVariables,
    setGlobalAggregationVariables,
    setRegularReducerVariables,
  ]);

  if (pcIsLoading) {
    return (
      <PageContent withAds>
        <Loader />
      </PageContent>
    );
  }

  return (
    <>
      {pcIsSuccess ? (
        <RouteContext.Provider value={routeContextValue}>
          <ProductCollection data={pcData} aggData={aggData ?? []} />
        </RouteContext.Provider>
      ) : null}
    </>
  );
};

export default ProductCollectionLoader;
